
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { categorizeAikenQuestionDifficulty } from '@/services/quiz-aiken-service';

interface QuestionPreview {
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  option_e?: string;
  difficulty: string;
}

interface AikenPreviewProps {
  content: string;
}

const AikenPreview: React.FC<AikenPreviewProps> = ({ content }) => {
  const [questions, setQuestions] = useState<QuestionPreview[]>([]);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!content.trim()) {
      setQuestions([]);
      setError(null);
      return;
    }

    try {
      // Simple Aiken format parsing to preview difficulty
      const lines = content.split('\n');
      const parsedQuestions: QuestionPreview[] = [];
      let currentQuestion: Partial<QuestionPreview> = {};
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Empty line indicates potential new question
        if (!line) continue;
        
        // If line doesn't start with A., B., C., D., or ANSWER:, it's a question
        if (!line.match(/^[A-E]\.\s/) && !line.match(/^ANSWER:\s*[A-E]$/i)) {
          if (Object.keys(currentQuestion).length > 0) {
            // If we were building a question, save it and start a new one
            if (currentQuestion.question_text && 
                currentQuestion.option_a && 
                currentQuestion.option_b && 
                currentQuestion.option_c && 
                currentQuestion.option_d) {
              // Calculate difficulty
              const difficulty = categorizeAikenQuestionDifficulty({
                question_text: currentQuestion.question_text || '',
                option_a: currentQuestion.option_a || '',
                option_b: currentQuestion.option_b || '',
                option_c: currentQuestion.option_c || '',
                option_d: currentQuestion.option_d || '',
                option_e: currentQuestion.option_e
              });
              
              parsedQuestions.push({
                ...currentQuestion as QuestionPreview,
                difficulty
              });
            }
            currentQuestion = {};
          }
          currentQuestion.question_text = line;
        } 
        // Parse options
        else if (line.startsWith('A.')) {
          currentQuestion.option_a = line.substring(2).trim();
        }
        else if (line.startsWith('B.')) {
          currentQuestion.option_b = line.substring(2).trim();
        }
        else if (line.startsWith('C.')) {
          currentQuestion.option_c = line.substring(2).trim();
        }
        else if (line.startsWith('D.')) {
          currentQuestion.option_d = line.substring(2).trim();
        }
        else if (line.startsWith('E.')) {
          currentQuestion.option_e = line.substring(2).trim();
        }
        // Process answer and finalize the question
        else if (line.match(/^ANSWER:\s*[A-E]$/i)) {
          if (currentQuestion.question_text && 
              currentQuestion.option_a && 
              currentQuestion.option_b && 
              currentQuestion.option_c && 
              currentQuestion.option_d) {
                
            // Calculate difficulty
            const difficulty = categorizeAikenQuestionDifficulty({
              question_text: currentQuestion.question_text || '',
              option_a: currentQuestion.option_a || '',
              option_b: currentQuestion.option_b || '',
              option_c: currentQuestion.option_c || '',
              option_d: currentQuestion.option_d || '',
              option_e: currentQuestion.option_e
            });
            
            parsedQuestions.push({
              ...currentQuestion as QuestionPreview,
              difficulty
            });
          }
          currentQuestion = {};
        }
      }
      
      // Add the last question if it's complete
      if (currentQuestion.question_text && 
          currentQuestion.option_a && 
          currentQuestion.option_b && 
          currentQuestion.option_c && 
          currentQuestion.option_d) {
        
        // Calculate difficulty
        const difficulty = categorizeAikenQuestionDifficulty({
          question_text: currentQuestion.question_text || '',
          option_a: currentQuestion.option_a || '',
          option_b: currentQuestion.option_b || '',
          option_c: currentQuestion.option_c || '',
          option_d: currentQuestion.option_d || '',
          option_e: currentQuestion.option_e
        });
        
        parsedQuestions.push({
          ...currentQuestion as QuestionPreview,
          difficulty
        });
      }
      
      setQuestions(parsedQuestions);
      setError(null);
    } catch (err) {
      setError("Error parsing Aiken format");
      console.error('Error parsing Aiken content:', err);
    }
  }, [content]);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  
  if (questions.length === 0) {
    return null;
  }

  const difficultyDistribution = {
    easy: questions.filter(q => q.difficulty === 'easy').length,
    medium: questions.filter(q => q.difficulty === 'medium').length,
    hard: questions.filter(q => q.difficulty === 'hard').length,
  };
  
  const total = questions.length;
  
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium">
          Aiken Import Preview ({total} questions)
        </CardTitle>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-100">
            Easy: {difficultyDistribution.easy} ({Math.round(difficultyDistribution.easy / total * 100)}%)
          </Badge>
          <Badge variant="outline" className="bg-amber-50 text-amber-700 hover:bg-amber-100">
            Medium: {difficultyDistribution.medium} ({Math.round(difficultyDistribution.medium / total * 100)}%)
          </Badge>
          <Badge variant="outline" className="bg-red-50 text-red-700 hover:bg-red-100">
            Hard: {difficultyDistribution.hard} ({Math.round(difficultyDistribution.hard / total * 100)}%)
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="max-h-60 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="border-b">
              <tr>
                <th className="text-left py-2">#</th>
                <th className="text-left py-2">Question Preview</th>
                <th className="text-left py-2">Difficulty</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q, idx) => (
                <tr key={idx} className="border-b last:border-0">
                  <td className="py-2">{idx + 1}</td>
                  <td className="py-2 max-w-xs truncate">
                    {q.question_text?.substring(0, 60)}
                    {q.question_text && q.question_text.length > 60 ? '...' : ''}
                  </td>
                  <td className="py-2">
                    <Badge
                      className={
                        q.difficulty === 'easy' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                        q.difficulty === 'medium' ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' :
                        'bg-red-100 text-red-800 hover:bg-red-200'
                      }
                    >
                      {q.difficulty}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AikenPreview;
